import { useEffect } from 'react'

import { addBreadcrumb, captureException, startSpan } from '@sentry/react'
import { type DefaultError, useMutation, type UseMutationOptions } from '@tanstack/react-query'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'
import { getApp } from 'firebase/app'
import { type FunctionsError, getFunctions, httpsCallable } from 'firebase/functions'

import {
  canProceed,
  confirmSessionThenOpenDialog,
  markSessionExpired,
  pinnedPiloteSession,
} from '@/auth/sessionGuard/sessionStore.ts'

export type OnrApiMutationOptions<
  TData = unknown,
  TVariables = void,
  TError = DefaultError,
  TOnMutateResult = unknown,
> = Omit<UseMutationOptions<TData, TError, TVariables, TOnMutateResult>, 'mutationFn'> & {
  reportErrorToSentry?: boolean
  // Undoes what onMutate wrote. It runs for session errors too, which skip onError.
  cleanUpAfterError?: (variables: TVariables) => Promise<unknown>
}

interface ApiRequest<TVariables> {
  readonly type: string
  readonly value: TVariables
  readonly piloteSession?: string
}

export class SessionExpiredError extends Error {
  constructor() {
    super('The Pilote session has expired')
    this.name = 'SessionExpiredError'
  }
}

const SESSION_ERROR_CODES = new Set(['functions/failed-precondition', 'functions/unauthenticated'])

const isFunctionsError = (error: unknown): error is FunctionsError =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  typeof (error as FunctionsError).code === 'string' &&
  (error as FunctionsError).code.startsWith('functions/')

export const isSessionError = (error: unknown) =>
  error instanceof SessionExpiredError || (isFunctionsError(error) && SESSION_ERROR_CODES.has(error.code))

const assertSessionUsable = () => {
  if (!canProceed()) {
    handleNotAwaitedPromise(confirmSessionThenOpenDialog())
    throw new SessionExpiredError()
  }
}

export function useOnrApi<TData = unknown, TVariables = void, TError = DefaultError, TOnMutateResult = unknown>({
  type,
  onError,
  onSuccess,
  onMutate,
  reportErrorToSentry = true,
  cleanUpAfterError,
  ...options
}: {
  type: string
} & OnrApiMutationOptions<TData, TVariables, TError, TOnMutateResult>) {
  const api = httpsCallable<ApiRequest<TVariables>, TData>(getFunctions(getApp(), 'northamerica-northeast1'), 'onr-api')
  const mutation = useMutation<TData, TError, TVariables, TOnMutateResult>({
    onMutate: (variables, context) => onMutate?.(variables, context) as TOnMutateResult | Promise<TOnMutateResult>,
    mutationFn: async (value) => {
      assertSessionUsable()
      const piloteSession = pinnedPiloteSession()
      const result = await startSpan({ name: `onr-api`, attributes: { type } }, () =>
        api(piloteSession ? { type, value, piloteSession } : { type, value })
      )
      return result.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      addBreadcrumb({
        category: 'onr-api',
        message: `invoke onr-api ${type}`,
        data: {
          type,
          variables,
          data,
          context,
        },
      })
      return onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: async (error, variables, onMutateResult, context) => {
      const sessionError = isSessionError(error)
      // A local refusal already asked Pilote, whose answer decides whether the dialog opens.
      if (sessionError && !(error instanceof SessionExpiredError)) {
        markSessionExpired()
      } else if (!sessionError && reportErrorToSentry) {
        captureException(error, { tags: { type }, fingerprint: ['onr-api', type], extra: { variables, context } })
      }
      await cleanUpAfterError?.(variables)
      return sessionError ? null : onError?.(error, variables, onMutateResult, context)
    },
    ...options,
  })
  // The dialog speaks for a session error: an inline "could not be saved" reading the mutation
  // state would only repeat it, and stay once the dialog is closed.
  const { error, reset } = mutation
  useEffect(() => {
    if (isSessionError(error)) {
      reset()
    }
  }, [error, reset])
  return mutation
}
